<template>
  <pn-toast
    v-if="errorMsg"
    temporary
    :text="errorMsg"
    appearance="error"
    @close="errorMsg = ''"
  ></pn-toast>
  <h2>Edit Price List</h2>
  <form class="edit-price-list-form" @submit.prevent="handleUpdate">
    <pn-input
      label="Name"
      name="priceListName"
      v-model="formValues.priceListName"
    ></pn-input>
    <pn-input
      label="Type"
      name="type"
      v-model="formValues.priceListType"
    ></pn-input>
    <pn-input
      label="Currency Code"
      name="currencyCode"
      v-model="formValues.currencyCode"
    ></pn-input>
    <pn-date-picker
      label-from="Effective From"
      :start="formValues.effectiveFrom"
      @dateSelection="selectDate($event, 'effectiveFrom')"
    ></pn-date-picker>
    <pn-date-picker
      label-from="Effective To"
      :start="formValues.effectiveTo"
      @dateSelection="selectDate($event, 'effectiveTo')"
    ></pn-date-picker>
    <pn-select label="Status" name="status" @change="changeStatus">
      <option value="Active" :selected="formValues.status === 'Active'">
        Active
      </option>
      <option value="Draft" :selected="formValues.status === 'Draft'">
        Draft
      </option>
      <option value="Inactive" :selected="formValues.status === 'Inactive'">
        Inactive
      </option>
    </pn-select>
    <div class="form-actions">
      <pn-button type="submit" :loading="loading">Update</pn-button>
      <pn-button
        appearance="light"
        variant="outlined"
        type="button"
        @click="handleCancel"
        >Cancel</pn-button
      >
    </div>
  </form>
</template>

<script setup lang="ts">
import { reactive, computed, ref } from "vue";
import { storeToRefs } from "pinia";
import constant from "@/constant";
import { usePriceManagementStore } from "@/stores/priceManagement";
import type { PnDatePickerSelectEvent } from "@postnord/web-components/types/components";
import type { PriceManagementUpdatePriceListRequest } from "@/types/priceManagement";

const { PRICE_MANAGEMENT } = constant;

const props = defineProps<{
  priceListId: string;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "close", payload: { status: "success"; message: string }): void;
}>();

const priceManagementStore = usePriceManagementStore();
const { priceLists } = storeToRefs(priceManagementStore);
const errorMsg = ref("");
const loading = ref(false);

const selectedPricing = computed(() =>
  priceLists.value.find((pl) => pl.id === props.priceListId),
);

const toDateString = (value: string | null | undefined): string => {
  if (!value) return "";
  return value.slice(0, 10);
};

const formValues = reactive({
  priceListName: selectedPricing.value?.priceList?.priceListName ?? "",
  priceListType: selectedPricing.value?.priceList?.priceListType ?? "",
  currencyCode: selectedPricing.value?.priceList?.currencyCode ?? "",
  effectiveFrom: toDateString(selectedPricing.value?.priceList?.effectiveFrom),
  effectiveTo: toDateString(selectedPricing.value?.priceList?.effectiveTo),
  status: selectedPricing.value?.priceList?.status ?? "Draft",
});

const hasEmptyRequiredFields = (): boolean => {
  return (
    !formValues.priceListName.trim() ||
    !formValues.currencyCode.trim() ||
    !formValues.effectiveFrom.trim() ||
    !formValues.effectiveTo.trim()
  );
};

const hasDuplicatePriceListName = (): boolean => {
  const normalizedName = formValues.priceListName.trim().toLowerCase();
  return priceLists.value.some((priceList) => {
    if (priceList.id === props.priceListId) {
      return false;
    }
    return (
      priceList.priceList.priceListName.trim().toLowerCase() === normalizedName
    );
  });
};

const hasAtLeastOneActivePriceList = (): boolean => {
  const selectedPriceList = selectedPricing.value;
  if (!selectedPriceList) {
    return false;
  }
  if (formValues.status === "Active") {
    return true;
  }
  return priceLists.value.some((priceList) => {
    if (priceList.id === props.priceListId) {
      return false;
    }
    return priceList.priceList.status === "Active";
  });
};

const hasAnyFieldChanged = (): boolean => {
  const pricingToUpdate = selectedPricing.value;
  if (!pricingToUpdate) {
    return true;
  }
  const originalName = pricingToUpdate.priceList.priceListName.trim();
  const originalType = pricingToUpdate.priceList.priceListType.trim();
  const originalCurrencyCode = pricingToUpdate.priceList.currencyCode.trim();
  const originalEffectiveFrom = toDateString(
    pricingToUpdate.priceList.effectiveFrom,
  );
  const originalEffectiveTo = toDateString(
    pricingToUpdate.priceList.effectiveTo,
  );
  const originalStatus = pricingToUpdate.priceList.status;
  return (
    formValues.priceListName.trim() !== originalName ||
    formValues.priceListType.trim() !== originalType ||
    formValues.currencyCode.trim() !== originalCurrencyCode ||
    formValues.effectiveFrom !== originalEffectiveFrom ||
    formValues.effectiveTo !== originalEffectiveTo ||
    formValues.status !== originalStatus
  );
};

const handleUpdate = async () => {
  errorMsg.value = "";

  if (hasEmptyRequiredFields()) {
    errorMsg.value = "All fields are required.";
    return;
  }

  if (hasDuplicatePriceListName()) {
    errorMsg.value =
      "Price list name already exists. Please use a unique name.";
    return;
  }

  if (!hasAtLeastOneActivePriceList()) {
    errorMsg.value = "At least one price list must remain active.";
    return;
  }

  const pricingToUpdate = selectedPricing.value;
  if (!pricingToUpdate) {
    errorMsg.value = "Unable to find selected price list.";
    return;
  }

  if (!hasAnyFieldChanged()) {
    emit("close");
    return;
  }

  const payload: PriceManagementUpdatePriceListRequest = {
    priceListId: pricingToUpdate.priceList.id,
    priceListName: formValues.priceListName.trim(),
    priceListType: formValues.priceListType.trim(),
    currencyCode: formValues.currencyCode.trim(),
    effectiveFrom: formValues.effectiveFrom,
    effectiveTo: formValues.effectiveTo || null,
    status: formValues.status,
    isActive: formValues.status === "Active",
  };

  try {
    loading.value = true;
    const updateResult = await priceManagementStore.updatePriceList(payload);
    if (updateResult.status === "error") {
      errorMsg.value = updateResult.message;
      return;
    }

    emit("close", {
      status: "success",
      message: PRICE_MANAGEMENT.UPDATE_SUCCESS_MESSAGE,
    });
  } catch (error) {
    errorMsg.value = "An unexpected error occurred.";
  } finally {
    loading.value = false;
  }
};

const handleCancel = () => {
  emit("close");
};

const selectDate = (
  event: CustomEvent<PnDatePickerSelectEvent>,
  field: "effectiveFrom" | "effectiveTo",
) => {
  const selectedDate = event.detail.start;
  if (selectedDate) {
    formValues[field] = selectedDate;
  }
};

const changeStatus = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  formValues.status = target.value;
};
</script>

<style scoped>
.edit-price-list-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  /* width: 100%; */
}

.edit-price-list-form :deep(pn-input),
.edit-price-list-form :deep(pn-select),
.edit-price-list-form :deep(pn-date-picker) {
  width: 400px;
  /* max-width: 100%; */
}
.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
</style>
